import { useState, useRef, useEffect, useMemo } from "react";
import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import { useDataStore } from "@store/user_org";

const TEAM_LABELS: Record<string, string> = {
  mecanique: "Mécanique",
  carrosserie: "Carrosserie",
  other: "Autres",
};

const WORKSHOP_ROLES = new Set(["mecanicien", "carrossier", "chef_atelier"]);

interface AssignDropdownProps {
  /** Canonical list of assigned user ids. */
  assignedIds: string[];
  /** Called with the full new list of ids whenever the selection changes. */
  onChange: (userIds: string[]) => void;
}

export default function AssignDropdown({ assignedIds, onChange }: AssignDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const orgMembers = useDataStore((s) => s.orgMembers);

  const hasAssignees = assignedIds.length > 0;
  const nameFor = (id: string) => {
    const m = orgMembers.find((mm) => mm.id === id);
    return m ? (m.infos.display_name || m.infos.full_name) : id;
  };
  // Button label: single name, or "N assignés" when multiple.
  const label = !hasAssignees
    ? "Non assigné"
    : assignedIds.length === 1
      ? nameFor(assignedIds[0])
      : `${assignedIds.length} assignés`;

  const toggle = (userId: string) => {
    if (assignedIds.includes(userId)) {
      onChange(assignedIds.filter((id) => id !== userId));
    } else {
      onChange([...assignedIds, userId]);
    }
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Group active members by team, sorted alphabetically
  const grouped = useMemo(() => {
    const active = orgMembers.filter(
      (m) => m.status !== "inactive" && m.config?.access_type !== "GUEST",
    );

    const groups: Record<string, typeof active> = { mecanique: [], carrosserie: [], other: [] };
    for (const m of active) {
      const team = m.config?.team;
      if (team === "mecanique" || team === "carrosserie") {
        groups[team].push(m);
      } else if (WORKSHOP_ROLES.has(m.role)) {
        // Fallback: role-based grouping
        if (m.role === "carrossier") groups.carrosserie.push(m);
        else groups.mecanique.push(m);
      } else {
        groups.other.push(m);
      }
    }

    // Sort each group alphabetically
    const sortByName = (a: typeof active[0], b: typeof active[0]) =>
      (a.infos.display_name || a.infos.full_name || "").localeCompare(
        b.infos.display_name || b.infos.full_name || "",
        "fr",
      );
    for (const key of Object.keys(groups)) {
      groups[key].sort(sortByName);
    }

    // Return only non-empty groups in order
    return ["mecanique", "carrosserie", "other"]
      .filter((key) => groups[key].length > 0)
      .map((key) => ({ key, label: TEAM_LABELS[key], members: groups[key] }));
  }, [orgMembers]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex flex-row items-center gap-1.5 text-h3 font-medium cursor-pointer transition-opacity hover:opacity-80 rounded-full px-3 py-1"
        style={{
          background: hasAssignees ? "#E0E7FF" : "#F4F4F4",
          color: hasAssignees ? "#3730A3" : "#A1A1A1",
        }}
      >
        <Icon1 name="user" size={12} color={hasAssignees ? "#3730A3" : "#A1A1A1"} />
        {label}
        <svg width={8} height={5} viewBox="0 0 8 5" fill="none">
          <path d="M1 1l3 3 3-3" stroke={hasAssignees ? "#3730A3" : "#A1A1A1"} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden overflow-y-auto"
          style={{ minWidth: 220, maxHeight: 320 }}
        >
          {hasAssignees && (
            <button
              className="flex flex-row items-center gap-2.5 w-full px-3 py-2 hover:bg-neutral-50 transition-colors text-left border-b border-neutral-100"
              onClick={() => onChange([])}
            >
              <Icon1 name="close" size={14} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Tout retirer</span>
            </button>
          )}
          {grouped.map((group) => (
            <div key={group.key}>
              <div className="px-3 py-1.5 text-h4 font-semibold text-neutral-400 uppercase tracking-wide bg-neutral-50">
                {group.label}
              </div>
              {group.members.map((member) => {
                const name = member.infos.display_name || member.infos.full_name;
                const isSelected = assignedIds.includes(member.id);
                return (
                  <button
                    key={member.id}
                    className="flex flex-row items-center gap-2.5 w-full px-3 py-2 hover:bg-neutral-50 transition-colors text-left"
                    onClick={() => toggle(member.id)}
                  >
                    <div className="flex-shrink-0" style={{ borderRadius: 999 }}>
                      <UserAvatar
                        src={member.customization?.photo_url}
                        value={member.customization?.avatar_seed || name}
                        size={24}
                        className="w-6 h-6 rounded-full"
                      />
                    </div>
                    <span className="text-h3 font-medium flex-1 truncate">{name}</span>
                    {isSelected && <Icon1 name="done2" size={16} color="#5C6BFF" />}
                  </button>
                );
              })}
            </div>
          ))}
          {grouped.length === 0 && (
            <div className="px-3 py-3 text-h3 text-neutral-400 text-center">Aucun membre</div>
          )}
        </div>
      )}
    </div>
  );
}
